'use client';

import { inputVariants } from '@zealot/ui/components/input';
import { cn } from '@zealot/ui/lib/utils';
import * as React from 'react';
import { HexColorInput } from 'react-colorful';

import { useColorPicker } from './color-picker';
import { hexToRgbaString, parseRgba, rgbaToHex } from './color-picker-utils';

type ColorPickerInputProps = Omit<React.ComponentProps<'input'>, 'value' | 'onChange' | 'size' | 'color'> & {
  size?: 'sm' | 'default' | 'lg';
};

// gloss
const ColorPickerInput = ({
  className,
  size = 'default',
  'aria-label': ariaLabel,
  ...props
}: ColorPickerInputProps) => {
  const { color, isEmpty, format, onChange, fieldProps } = useColorPicker();
  const typedHex = React.useRef<string | null>(null);
  const rgba = format === 'rgba' ? parseRgba(color) : null;
  const isShowingTypedHex =
    rgba !== null && typedHex.current !== null && hexToRgbaString(typedHex.current, rgba.a) === color;
  const hex = rgba ? (isShowingTypedHex ? typedHex.current : rgbaToHex(rgba)) : color;

  const handleChange = (nextHex: string) => {
    if (!rgba) {
      onChange(nextHex);
      return;
    }

    typedHex.current = nextHex;
    onChange(hexToRgbaString(nextHex, rgba.a));
  };

  return (
    <HexColorInput
      data-slot="color-picker-input"
      aria-label={ariaLabel ?? (fieldProps.id ? undefined : 'Hex color')}
      placeholder="#000000"
      prefixed={!isEmpty}
      color={isEmpty ? '' : (hex ?? '')}
      onChange={handleChange}
      className={cn(inputVariants({ size }), 'font-mono uppercase', className)}
      {...fieldProps}
      {...props}
    />
  );
};

export type { ColorPickerInputProps };
export { ColorPickerInput };
